/**
 * Strategy Establishment — its own full page, one of the three Draft Board
 * sections (see pages/DraftBoard.jsx, the landing/picker page this is
 * reached from). Draft Board rebuild Stage 1 — see
 * engineering/ARCHIVE/completed-prompts/JAI_DraftBoard_Rebuild_Prompt.md.
 *
 * Renders one of four states from GET /draft/context, decided before
 * anything else renders:
 *   A — no strategy, draft not complete  → the mascot flow
 *   B — locked strategy exists           → the locked strategy view
 *   C — draft complete, no strategy      → the reverse-engineer path
 *   D — auction draft detected           → unsupported message
 */

import CreditCost from '../../components/CreditCost'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useLeagues } from '../../context/LeagueContext'
import { draftBoardApi } from '../../services/api'
import { PLAN_STRATEGY_COUNT } from '../../config/planAccess'
import AppShell from '../../components/AppShell'
import ToolHeader from '../../components/ToolHeader'
import { JAI_FONTS } from '../../components/JaiBrand'
import { V2, V2Card, V2SectionHead, pctTone, Notice } from '../../components/JaiV2'
import { JAIMascot } from '../../components/JAIMascot'
import MascotDialog from '../../components/draft/MascotDialog'
import StrategyDetailView from './StrategyDetailView'
import PickTradesDialog from './PickTradesDialog'
import { IS_NATIVE_APP } from '../../services/runtime'
import OfflineBanner from '../../components/OfflineBanner'
import { trackEvent } from '../../analytics'
import { useMinWidth } from '../../hooks/useMinWidth'
import { saveOfflineCache, loadOfflineCache, isNetworkError } from '../../services/offlineCache'

/**
 * What to actually tell the user about a failed request.
 *
 * A timeout is a genuinely different situation from a broken one: it's the
 * slow-or-hung case, nothing was saved, and trying again very often works —
 * so it gets its own wording rather than the generic error. Server-sent
 * messages win when there is one (they're more specific than anything we can
 * guess here); the local fallbacks cover the case where the server never
 * answered at all, which is exactly when axios gives up on its own.
 */
const errorMessage = (err, fallback) => {
  // A build that outlasted the original request reports its failure through
  // the polled list (see waitForBuild), not through an HTTP error.
  if (err?.buildError) return err.buildError
  const serverMessage = err?.response?.data?.error
  const timedOut = err?.code === 'ECONNABORTED' || err?.response?.status === 504
  if (timedOut) {
    return serverMessage || 'JAI is taking longer than usual and stopped. Nothing was saved — try again.'
  }
  return serverMessage || fallback
}

// A strategy build can take longer than one web request is allowed to (Render
// cuts requests off at ~100s), so the server answers 202 "still building" and
// the page checks back with a cheap GET until it is done.
const BUILD_POLL_MS = 2000
const BUILD_GIVE_UP_MS = 6 * 60 * 1000
const MAX_POLL_FAILURES = 4
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const ORDINAL = (n) => {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`
}

// ── Confirm-details field list ─────────────────────────────────────────────

const FIELD_LABELS = {
  scoringFormat: 'Scoring format',
  teamCount: 'Number of teams',
  draftType: 'Draft type',
  totalRounds: 'Total rounds',
  firstRoundPick: 'Your first-round pick',
}

// Kept in sync with backend/src/services/draftContextService.js's
// NOTES_MAX_LENGTH — a client-side mismatch would only mean an extra
// round-trip truncation, but matching avoids that surprise entirely.
const NOTES_MAX_LENGTH = 600

// Display labels for the draftType values suggestDraftTypeFromNotes can
// return — matches RECOGNIZED_DRAFT_TYPES in draftContextService.js.
const DRAFT_TYPE_DISPLAY = {
  snake: 'Snake',
  third_round_reversal: 'Third Round Reversal',
  linear: 'Linear',
  auction: 'Auction',
}

// <input type="datetime-local"> speaks local wall-clock text ("2026-08-30T19:00"),
// the server stores a real moment in time. These two convert between them.
const isoToLocalInput = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
const localInputToIso = (value) => {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

const LEAGUE_TYPE_OPTIONS = [
  { value: 'redraft', label: 'Redraft' },
  { value: 'keeper', label: 'Keeper' },
  { value: 'dynasty', label: 'Dynasty' },
]

const LEAGUE_TYPE_HINT = {
  keeper: 'JAI will weigh who is worth keeping into next season, not just this season. Put your keeper rules in the notes below.',
  dynasty: "JAI will plan for the long term and treat ADP as a one-season market. If this is a rookie-only draft, say so in the notes — JAI's player pool isn't limited to rookies yet.",
}

/**
 * The "JAI is working on it" state: a clearly animated mascot, plain words about
 * what is happening, and (for a strategy build) how many are ready. Used for
 * the strategy build, the custom-strategy box and "find out my strategy".
 * `role="status"` so a screen reader announces it too.
 */
function JaiWorking({ headline, sub, done, total, compact = false }) {
  const showBar = total > 0
  return (
    <div
      role="status" aria-live="polite" data-testid="jai-working"
      style={{ textAlign: 'center', padding: compact ? '18px 0' : '40px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      <JAIMascot size={compact ? 76 : 128} state="thinking" animate />
      <div style={{ fontSize: compact ? 14 : 16, fontWeight: 800, color: V2.navy, marginTop: 10 }}>{headline}</div>
      {showBar && (
        <>
          <div style={{ width: 220, height: 6, borderRadius: 999, background: 'rgba(10,31,68,0.08)', marginTop: 12, overflow: 'hidden' }}>
            <div style={{ width: `${Math.max(8, Math.round((done / total) * 100))}%`, height: '100%', background: V2.blue, borderRadius: 999, transition: 'width 400ms ease' }} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: V2.blue, marginTop: 8 }} data-testid="jai-working-count">
            {done === 0 ? 'Getting started…' : `${done} of ${total} ready`}
          </div>
        </>
      )}
      {sub && <div style={{ fontSize: 12, color: V2.faint, marginTop: 8, maxWidth: 360, lineHeight: 1.5 }}>{sub}</div>}
    </div>
  )
}

function ContextFieldRow({ field, value, editing, onChange, missing }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: `1px solid ${V2.hairSoft}`, gap: 12 }}>
      <span style={{ fontSize: 12.5, color: V2.mute }}>{FIELD_LABELS[field]}</span>
      {editing ? (
        <input
          value={value ?? ''}
          onChange={(e) => onChange(field, e.target.value)}
          autoFocus={missing}
          style={{
            width: 140, textAlign: 'right', fontSize: 13, fontWeight: 700, color: V2.navy,
            border: `1px solid ${missing ? V2.error : V2.hairSoft}`, borderRadius: 7, padding: '4px 8px',
            fontFamily: JAI_FONTS.tight, outline: 'none',
          }}
        />
      ) : (
        <span style={{ fontSize: 13, fontWeight: 700, color: missing ? V2.error : V2.navy }}>
          {value ?? 'Data not found'}
        </span>
      )}
    </div>
  )
}

// Honest heads-ups for league formats JAI's strategies don't fully fit yet.
// Read from the settings a platform synced (Sleeper so far).
function formatNotes(league) {
  const rules = league?.leagueRules
  if (!rules) return []
  const notes = []
  if (rules.format?.guillotine) notes.push("This is a Chopped (guillotine) league. JAI's strategies assume a normal season-long format, so treat them as a rough guide.")
  if (rules.format?.bestBall) notes.push("This is a best ball league. Lineups set themselves, so JAI's weekly lineup and waiver advice won't apply; depth and upside matter most.")
  const traded = rules.draft?.tradedPicks
  if (traded) {
    const got = traded.acquired || []
    const gone = traded.tradedAway || []
    const bits = []
    if (got.length) bits.push(`you hold extra pick${got.length === 1 ? '' : 's'} in round ${got.map((p) => p.round).join(', ')}`)
    if (gone.length) bits.push(`you traded away your pick in round ${gone.map((p) => p.round).join(', ')}`)
    let note = `Pick trades: ${bits.join(' and ')}. JAI's plan below already reflects them.`
    if (got.some((p) => !p.fromSlot)) note = `Pick trades: ${bits.join(' and ')}. The draft order isn't set yet, so picks you traded for can't be placed in your plan until it is.`
    notes.push(note)
  }
  return notes
}

// Whether a change to the league details alters what the strategies are built
// from. The draft date only drives countdowns and reminders, so it doesn't.
function strategyInputsChanged(before, after) {
  if (!before || !after) return true
  const keys = new Set([...Object.keys(before), ...Object.keys(after)])
  keys.delete('draftDate')
  keys.delete('picks')
  for (const k of keys) {
    if ((before[k] ?? null) !== (after[k] ?? null)) return true
  }
  return false
}

// Which of the confirm-details fields are still blank. Used to block Confirm
// (the server refuses it too) and to send a user whose details were saved
// incomplete back to the dialog instead of leaving them stuck.
const blankFields = (f) => (f ? Object.keys(FIELD_LABELS).filter((k) => f[k] == null || f[k] === '' || Number.isNaN(f[k])) : [])

export default function StrategyEstablishment() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, plan, planLoading } = useAuth()
  const { leagues } = useLeagues()
  const league = leagues.find((l) => l.id === Number(id))
  const wide = useMinWidth(820)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [ctx, setCtx] = useState(null) // { state, context, lockedStrategy }

  // Mascot flow (state A)
  const [step, setStep] = useState('opener') // opener | confirm | list | detail | locking
  const [editing, setEditing] = useState(false)
  // True when the user came back to the details dialog from the strategy list
  // (the "Edit league details" button), not on first setup. `savedFields` is
  // what the strategies on screen were built from, so Save can tell whether a
  // rebuild is needed.
  const [reedit, setReedit] = useState(false)
  const [reeditLocked, setReeditLocked] = useState(false) // opened from the locked screen
  const [showPickTrades, setShowPickTrades] = useState(false)
  const [notice, setNotice] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const savedFields = useRef(null)
  const [fields, setFields] = useState(null)
  const [previewPicks, setPreviewPicks] = useState([])
  const previewTimer = useRef(null)
  // Set when the backend's keyword check (suggestDraftTypeFromNotes) finds
  // that `fields.notes` names a known draft type that disagrees with
  // `fields.draftType` — e.g. notes mention "third round reversal" but
  // Draft Type is still "snake". Advisory only; never applied on its own.
  const [draftTypeSuggestion, setDraftTypeSuggestion] = useState(null)

  const [strategies, setStrategies] = useState([])
  const [generating, setGenerating] = useState(false)
  // { done, total } while a build is running — drives "2 of 3 ready".
  const [buildProgress, setBuildProgress] = useState(null)
  // Generation failed and there's nothing to list. Tracked separately from
  // `error` so the list step can offer a retry instead of rendering an empty
  // recommendations list, which reads as "JAI had no ideas" rather than
  // "JAI didn't finish".
  const [strategiesFailed, setStrategiesFailed] = useState(false)
  // Non-null once the strategies list fell back to a cached snapshot from
  // an earlier successful visit (see services/offlineCache.js). Never set
  // when generateStrategies (a real AI call) is what's failing offline —
  // there's nothing honest to show in place of a build that was never
  // generated in the first place.
  const [strategiesOfflineInfo, setStrategiesOfflineInfo] = useState(null)
  const [customTurns, setCustomTurns] = useState({ used: 0, limit: null })
  const [customPrompt, setCustomPrompt] = useState('')
  const [customLoading, setCustomLoading] = useState(false)

  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [locking, setLocking] = useState(false)

  const [lockedDetail, setLockedDetail] = useState(null)
  const [refreshing, setRefreshing] = useState(false)

  const [reverseLoading, setReverseLoading] = useState(false)

  const strategyCount = planLoading ? null : (PLAN_STRATEGY_COUNT[plan] ?? null)

  // StrictMode double-invokes effects in dev (mount, cleanup, mount again),
  // and loadContext is fired from one. Without this guard that means two
  // concurrent generateStrategies calls — the second one's "replace all
  // candidates" deletes the rows the first call just created, so a detail
  // page opened against the first call's now-deleted id 404s the moment you
  // try to lock it. The ref persists across the double-invoke (refs aren't
  // reset by it, only full unmounts), so the second call sees it's already
  // in flight and skips.
  const strategiesRequested = useRef(false)

  // The values every analytics event from this page carries. A ref (kept current
  // by the effect) so reading them inside the long-lived load callbacks neither
  // goes stale nor re-creates those callbacks when the plan finishes loading.
  const analyticsRef = useRef({})
  useEffect(() => { analyticsRef.current = { plan, sport: league?.sport || null } }, [plan, league?.sport])

  // Stops a polling loop from touching state after the user has left the page.
  // (Set in the effect body, not just cleared in cleanup, so StrictMode's
  // mount/cleanup/mount in dev doesn't leave it false.)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // Waits for a build that outlasted its request, handing each poll's result to
  // onUpdate so finished strategies show up while the rest are still being
  // written. Resolves with the final
  // GET /draft/strategies body, or null if the user left the page; throws an
  // error carrying `buildError` (the server's message) if the build failed.
  const waitForBuild = useCallback(async (onUpdate) => {
    const startedAt = Date.now()
    let failures = 0
    for (;;) {
      await sleep(BUILD_POLL_MS)
      if (!mountedRef.current) return null
      let data
      try {
        data = (await draftBoardApi.getStrategies(id)).data
        failures = 0
      } catch (err) {
        // One dropped poll must not look like a failed build — the build is
        // still running on the server.
        failures += 1
        if (failures >= MAX_POLL_FAILURES) throw err
        continue
      }
      const gen = data.generation
      // Show whatever has landed so far — strategies appear one by one.
      onUpdate?.(data)
      if (gen?.status === 'failed') {
        throw Object.assign(new Error(gen.error), { buildError: gen.error || 'JAI could not finish building that — try again.' })
      }
      if (gen?.status !== 'running') return data
      if (Date.now() - startedAt > BUILD_GIVE_UP_MS) {
        throw Object.assign(new Error('timeout'), { buildError: 'JAI is taking longer than usual and stopped. Nothing was saved — try again.' })
      }
    }
  }, [id])

  // ── Load ──────────────────────────────────────────────────────────────
  const loadStrategies = useCallback(async ({ rebuild = false } = {}) => {
    setError('')
    setStrategiesFailed(false)
    try {
      let res = await draftBoardApi.getStrategies(id)
      // A build is already running on the server (the page was refreshed or
      // reopened mid-build): wait for it rather than starting another.
      const showPartial = (d) => {
        setStrategies(d.strategies || [])
        setBuildProgress(d.generation?.progress || null)
      }
      if (res.data.generation?.status === 'running') {
        setGenerating(true)
        showPartial(res.data)
        const done = await waitForBuild(showPartial)
        if (!done) return
        res = { data: done }
      }
      setCustomTurns({ used: res.data.customTurnsUsed, limit: res.data.customTurnsLimit })
      // After an edit to the league details the saved strategies are stale, so
      // `rebuild` skips straight to generating a fresh set.
      if (!rebuild && res.data.strategies.length) {
        setStrategies(res.data.strategies)
        setStrategiesOfflineInfo(null)
        saveOfflineCache('draftStrategies', id, res.data.strategies)
        return
      }
      let gen
      const buildStartedAt = Date.now()
      trackEvent('draft_strategies_requested', { ...analyticsRef.current })
      try {
        gen = await draftBoardApi.generateStrategies(id)
      } catch (genErr) {
        // The server won't build strategies without every league detail. Send
        // the user back to the dialog to fill the blanks, rather than leaving
        // them on a "Try again" button that can never succeed.
        if (genErr?.response?.status === 400 && genErr.response.data?.missingFields?.length) {
          setEditing(true)
          setStep('confirm')
          setError('Fill in the highlighted league details to continue.')
          return
        }
        throw genErr
      }
      let built = gen.data.strategies
      if (gen.status === 202) {
        // Still building after the request's wait: keep checking until done,
        // showing each strategy as it lands.
        const done = await waitForBuild(showPartial)
        if (!done) return
        built = done.strategies
        if (!built.length) {
          // Idle with nothing saved: the server restarted mid-build.
          throw Object.assign(new Error('interrupted'), { buildError: 'The build was interrupted — try again.' })
        }
        setCustomTurns({ used: done.customTurnsUsed, limit: done.customTurnsLimit })
      }
      setStrategies(built)
      setStrategiesOfflineInfo(null)
      saveOfflineCache('draftStrategies', id, built)
      trackEvent('draft_strategies_ready', { ...analyticsRef.current, count: built.length, seconds: Math.round((Date.now() - buildStartedAt) / 1000) })
    } catch (err) {
      // Offline fallback — only for a genuine network drop (getStrategies
      // itself unreachable), never for generateStrategies failing offline:
      // that's a real AI call with nothing generated yet to fall back to,
      // so showing cached strategies from a DIFFERENT earlier build would
      // be actively misleading mid-draft.
      if (isNetworkError(err)) {
        const cached = await loadOfflineCache('draftStrategies', id)
        if (cached) {
          setStrategies(cached.data)
          setStrategiesOfflineInfo({ cachedAt: cached.cachedAt })
          return
        }
      }
      trackEvent('draft_strategies_failed', { ...analyticsRef.current, reason: err?.buildError ? 'build_error' : (err?.response?.status || err?.code || 'unknown') })
      setError(errorMessage(err, 'Could not generate strategies right now — try again in a moment.'))
      setStrategiesFailed(true)
    } finally {
      setGenerating(false)
      setBuildProgress(null)
    }
  }, [id, waitForBuild])

  const retryStrategies = () => {
    setGenerating(true)
    loadStrategies()
  }

  const loadContext = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await draftBoardApi.getContext(id)
      setCtx(res.data)
      setFields(res.data.context)
      setPreviewPicks(res.data.context.picks || [])

      if (res.data.state === 'A') {
        if (res.data.context.confirmedByUser && blankFields(res.data.context).length) {
          // Confirmed earlier with a field left blank (this used to be
          // allowed). Strategy building can't run without it, so reopen the
          // dialog with the blanks ready to fill in.
          setEditing(true)
          setStep('confirm')
        } else if (res.data.context.confirmedByUser) {
          setStep('list')
          if (strategiesRequested.current) return
          strategiesRequested.current = true
          // Set before the fire-and-forget call below so the list step
          // renders straight into the "Building your strategies…" mascot
          // state rather than flashing an empty list for a moment first.
          setGenerating(true)
          // Not awaited: strategy generation can take well over a minute,
          // and the page-level spinner should hand off to the "Building
          // your strategies…" mascot state below rather than sitting on
          // "Loading your draft…" for the whole call.
          loadStrategies()
        } else {
          setStep('opener')
        }
      } else if (res.data.state === 'B') {
        const d = await draftBoardApi.getStrategy(id, res.data.lockedStrategy.id)
        setLockedDetail(d.data.strategy)
      }
    } catch {
      setError('Failed to load draft context')
    } finally {
      setLoading(false)
    }
  }, [id, loadStrategies])

  useEffect(() => { loadContext() }, [loadContext])

  // ── Confirm-details step ─────────────────────────────────────────────
  // 'draftType', 'scoringFormat' and 'notes' are free text; everything else
  // in FIELD_LABELS is numeric.
  const STRING_FIELDS = new Set(['draftType', 'scoringFormat', 'notes', 'leagueType', 'draftDate'])

  const onFieldChange = (field, value) => {
    const next = { ...fields, [field]: typeof value === 'boolean' || STRING_FIELDS.has(field) ? value : (value === '' ? null : Number(value)) }
    setFields(next)
    clearTimeout(previewTimer.current)
    previewTimer.current = setTimeout(async () => {
      try {
        const res = await draftBoardApi.previewContext(id, next)
        setPreviewPicks(res.data.picks || [])
        setDraftTypeSuggestion(res.data.suggestedDraftType || null)
      } catch { /* leave the last known preview on a transient failure */ }
    }, 300)
  }

  const applyDraftTypeSuggestion = () => {
    if (!draftTypeSuggestion) return
    onFieldChange('draftType', draftTypeSuggestion)
    setDraftTypeSuggestion(null)
  }

  // ── Pick trades ─────────────────────────────────────────────────────
  // After the user records a trade, the server has already re-planned every
  // strategy around the new pick list; this just reloads what is on screen.
  const handlePickTradesSaved = async (data) => {
    setShowPickTrades(false)
    setCtx((prev) => (prev ? { ...prev, context: { ...prev.context, picks: data.picks, tradedPicks: null, manualPickTrades: data.manualPickTrades, platformTradedPicks: data.platformTradedPicks } } : prev))
    setPreviewPicks(data.picks || [])
    setFields((prev) => (prev ? { ...prev, picks: data.picks } : prev))
    const changed = (data.strategyChanges || []).filter((c) => c.added.length || c.removed.length || c.moved.length)
    setNotice(changed.length
      ? 'Pick trades saved. Your strategy was updated to match your new picks.'
      : 'Pick trades saved. Your pick list is updated.')
    try {
      if (ctx?.state === 'B' && lockedDetail) {
        const res = await draftBoardApi.getStrategy(id, lockedDetail.id)
        setLockedDetail(res.data.strategy)
      } else if (step === 'list' || step === 'detail') {
        const res = await draftBoardApi.getStrategies(id)
        if (res.data?.strategies?.length) setStrategies(res.data.strategies)
        if (step === 'detail') setStep('list')
      }
    } catch { /* the saved trades stand; the page refreshes on the next visit */ }
  }

  // /draft/strategy?trades=1 (a link from Live Draft) opens the editor.
  useEffect(() => {
    if (searchParams.get('trades') === '1' && !loading) {
      setShowPickTrades(true)
      const next = new URLSearchParams(searchParams)
      next.delete('trades')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, loading, setSearchParams])

  // Back to the details dialog from the strategy list.
  const startEditingDetails = ({ fromLocked = false } = {}) => {
    savedFields.current = { ...fields }
    setReeditLocked(fromLocked)
    setReedit(true)
    setEditing(true)
    setError('')
    setStep('confirm')
  }

  const cancelEditingDetails = () => {
    if (savedFields.current) setFields(savedFields.current)
    setPreviewPicks(ctx?.context?.picks || [])
    savedFields.current = null
    setReedit(false)
    setReeditLocked(false)
    setEditing(false)
    setError('')
    setStep('list')
  }

  const handleConfirmContext = async () => {
    if (blankFields(fields).length) {
      setEditing(true)
      setError('Fill in the highlighted league details to continue.')
      return
    }
    setError('')
    try {
      // Notes-vs-draftType mismatch is advisory (see draftTypeSuggestion
      // above) and never blocks a confirm — the user already had a chance
      // to see and act on it in the confirm step itself.
      const confirmed = await draftBoardApi.confirmContext(id, fields)
      trackEvent('draft_details_confirmed', {
        platform: league?.platform || null, sport: league?.sport || null, plan,
        league_type: fields.leagueType || 'redraft', has_draft_date: !!fields.draftDate,
      })
      // Keep the page's copy of the league details in step with what was just
      // saved. Without this the strategy detail screen kept the unconfirmed,
      // round-less copy until a reload, so a Starter user saw no "upgrade to
      // unlock" rows for the rounds beyond their plan.
      if (confirmed?.data?.context) setCtx((prev) => (prev ? { ...prev, context: confirmed.data.context } : prev))
      setEditing(false)
      setStep('list')
      if (reedit) {
        const fromLocked = reeditLocked
        setReedit(false)
        setReeditLocked(false)
        // Only things the strategies are built from force a rebuild. A rebuild
        // costs one request on the user's plan, so a changed draft date alone
        // just saves.
        const rebuild = strategyInputsChanged(savedFields.current, fields)
        savedFields.current = null
        if (!rebuild) return
        if (fromLocked) {
          // The locked strategy was built for the old details, so it can't stay
          // locked. Unlocking keeps it on record; the rebuild below replaces
          // the candidate list.
          await draftBoardApi.unlockStrategy(id, lockedDetail.id)
          setLockedDetail(null)
          setCtx((prev) => (prev ? { ...prev, state: 'A' } : prev))
        }
        setStrategies([])
        setGenerating(true)
        await loadStrategies({ rebuild: true })
        return
      }
      // Same reasoning as the loadContext path above: set before the list
      // step renders, so it shows the "Building your strategies…" mascot
      // instead of an empty recommendations list for the ~minute-plus
      // Claude generation actually takes.
      setGenerating(true)
      await loadStrategies()
    } catch (err) {
      setGenerating(false)
      setStep('confirm')
      setError(errorMessage(err, 'Failed to confirm your league details'))
    }
  }

  // ── Strategy list / detail ───────────────────────────────────────────
  const openStrategy = async (sid) => {
    setStep('detail')
    setDetailLoading(true)
    setError('')
    try {
      const res = await draftBoardApi.getStrategy(id, sid)
      setDetail(res.data.strategy)
    } catch (err) {
      // Without this the detail screen spun forever on a failed load.
      setStep('list')
      setError(errorMessage(err, 'Could not open that strategy — try again.'))
    } finally {
      setDetailLoading(false)
    }
  }

  const submitCustomPrompt = async () => {
    if (!customPrompt.trim()) return
    setCustomLoading(true)
    try {
      const res = await draftBoardApi.customStrategy(id, customPrompt.trim())
      if (res.status === 202) {
        // Still building after the request's wait — check back until it lands.
        const done = await waitForBuild()
        if (!done) return
        setStrategies(done.strategies)
        setCustomTurns({ used: done.customTurnsUsed, limit: done.customTurnsLimit })
      } else {
        setStrategies((prev) => [...prev, res.data.strategy])
        setCustomTurns({ used: res.data.customTurnsUsed, limit: res.data.customTurnsLimit })
      }
      setCustomPrompt('')
    } catch (err) {
      setError(errorMessage(err, 'Could not build that strategy — try rephrasing.'))
    } finally {
      setCustomLoading(false)
    }
  }

  const handleLock = async () => {
    setLocking(true)
    setStep('locking')
    setError('')
    try {
      const res = await draftBoardApi.lockStrategy(id, detail.id)
      trackEvent('draft_strategy_locked', { plan, sport: league?.sport || null })
      setLockedDetail(res.data.strategy)
    } catch (err) {
      if (err?.response?.status === 404) {
        // The strategy was replaced by a newer build (e.g. a second tab or a
        // retry regenerated the list) — show the current list, not a dead end.
        setError('That strategy was replaced by a newer build. Pick one from the updated list.')
        setStep('list')
        setDetail(null)
        loadStrategies()
      } else {
        setError(errorMessage(err, 'Could not lock this strategy — try again.'))
        setStep('detail')
      }
    } finally {
      setLocking(false)
    }
  }

  const handleChangeStrategy = async () => {
    try {
      await draftBoardApi.unlockStrategy(id, lockedDetail.id)
    } catch (err) {
      setError(errorMessage(err, 'Could not change your strategy — try again.'))
      return
    }
    setLockedDetail(null)
    await loadContext()
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      const res = await draftBoardApi.refreshStrategy(id, lockedDetail.id)
      setLockedDetail(res.data.strategy)
    } finally {
      setRefreshing(false)
    }
  }

  const handleReverse = async () => {
    setReverseLoading(true)
    try {
      const res = await draftBoardApi.reverseStrategy(id)
      setLockedDetail(res.data.strategy)
      setCtx((prev) => ({ ...prev, state: 'B' }))
    } catch (err) {
      // Every failure mode here (wrong season / unreadable draft / JAI
      // couldn't parse a strategy) previously showed this same generic
      // line — surface the server's actual message so they're distinguishable.
      setError(errorMessage(err, 'Could not analyse this draft right now — try again in a moment.'))
    } finally {
      setReverseLoading(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <AppShell>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', gap: 12 }}>
          <JAIMascot size={56} state="thinking" animate />
          <div style={{ fontSize: 13, color: V2.faint }}>Loading your draft…</div>
        </div>
      </AppShell>
    )
  }

  const pageShell = (children) => (
    <AppShell>
      <div style={{ padding: wide ? '30px 40px' : '20px 16px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <ToolHeader
            leagueName={league?.name}
            title="Strategy Establishment"
            back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
            backRight={{ label: 'Go to Draft Room', to: `/league/${id}/draft` }}
          />
          {notice && !error && (
            <Notice tone="success" role="status" data-testid="pick-trades-notice" action="Dismiss" onAction={() => setNotice('')} style={{ marginTop: 0 }}>{notice}</Notice>
          )}
          {error && (
            <Notice tone="error" style={{ marginTop: 0 }}>{error}</Notice>
          )}
          {children}
        </div>
      </div>
      {showPickTrades && <PickTradesDialog leagueId={id} onClose={() => setShowPickTrades(false)} onSaved={handlePickTradesSaved} />}
    </AppShell>
  )

  // State D — auction unsupported
  if (ctx?.state === 'D') {
    return pageShell(
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <JAIMascot size={140} state="idle" animate />
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: V2.navy, margin: '20px 0 8px' }}>JAI doesn't support auction drafts yet</h1>
        <p style={{ fontSize: 13.5, color: V2.mute, maxWidth: 380, margin: '0 auto' }}>
          Snake, third-round-reversal and linear drafts are covered — auction is on the roadmap.
        </p>
      </div>
    )
  }

  // State B — locked strategy view (or, for a league connected after its
  // draft finished, the reverse-engineered review of picks already made)
  if (ctx?.state === 'B' && lockedDetail && !(step === 'confirm' && reeditLocked)) {
    const isReverse = lockedDetail.source === 'reverse_engineered'
    return pageShell(
      <StrategyDetailView
        league={league} strategy={lockedDetail} mode={isReverse ? 'reverse' : 'locked'}
        totalRounds={ctx.context.totalRounds}
        onChangeStrategy={handleChangeStrategy}
        onEditDetails={isReverse ? undefined : () => startEditingDetails({ fromLocked: true })}
        onPickTrades={isReverse ? undefined : () => setShowPickTrades(true)}
        onRefresh={isReverse ? undefined : handleRefresh} refreshing={refreshing}
      />
    )
  }

  // State C — draft complete, no strategy
  if (ctx?.state === 'C') {
    if (reverseLoading) {
      return pageShell(
        <JaiWorking headline="JAI is analysing your draft" sub="Reading every pick you made to work out the strategy you actually ran." />
      )
    }
    return pageShell(
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <JAIMascot size={140} state="idle" animate />
        </div>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: V2.navy, margin: '20px 0 20px' }}>Your draft has already been completed.</h1>
        <button
          onClick={handleReverse}
          disabled={reverseLoading}
          style={{ background: V2.blue, color: 'white', border: 'none', borderRadius: 10, padding: '11px 22px', fontSize: 13.5, fontWeight: 700, cursor: reverseLoading ? 'default' : 'pointer', opacity: reverseLoading ? 0.6 : 1 }}
        >
          {reverseLoading ? 'Analysing your draft…' : 'Get JAI to find out your strategy'}
        </button>
      </div>
    )
  }

  // State A — the mascot flow
  if (step === 'opener') {
    const firstName = user?.firstName
    return pageShell(
      <MascotDialog
        state="happy" size={200}
        headline={firstName ? `Hi ${firstName}.` : 'Hi there.'}
        body="Ready to set your strategy for the season?"
        actions={[{ label: 'Continue', primary: true, onClick: () => { if (blankFields(fields).length) setEditing(true); setStep('confirm') } }]}
        onClose={() => {}} closable={false}
      />
    )
  }

  if (step === 'confirm' && fields) {
    return pageShell(
      <MascotDialog
        state="idle" size={110} width={440}
        headline="Confirm your league details"
        body={reedit ? undefined : (
          <span>Confirming starts building your strategies, the biggest job JAI does. <CreditCost tool="draftStrategy" /></span>
        )}
        onClose={() => {}} closable={false}
        actions={[
          { label: reedit ? 'Save changes' : 'Confirm', primary: true, onClick: handleConfirmContext, disabled: blankFields(fields).length > 0 },
          ...(reedit
            ? [{ label: 'Cancel', onClick: cancelEditingDetails }]
            : [{ label: editing ? 'Done editing' : 'Edit details', onClick: () => setEditing((v) => !v) }]),
        ]}
      >
        <div style={{ textAlign: 'left', marginTop: 18 }}>
          {formatNotes(league).map((note) => (
            <div key={note} data-testid="league-format-note" style={{
              fontSize: 12, color: V2.navy, lineHeight: 1.5, marginBottom: 10,
              borderLeft: '3px solid #9A6A00', borderRadius: 0, padding: '2px 0 2px 10px',
            }}>
              {note}
            </div>
          ))}
          {reeditLocked && (
            <div style={{ fontSize: 12, color: V2.mute, lineHeight: 1.5, marginBottom: 10 }}>
              Changing anything other than the draft date unlocks your current strategy and builds new ones for you to choose from.
            </div>
          )}
          {Object.keys(FIELD_LABELS).map((f) => (
            <ContextFieldRow key={f} field={f} value={fields[f]} editing={editing} onChange={onFieldChange} missing={fields[f] == null} />
          ))}
          <div style={{ padding: '10px 0', borderBottom: `1px solid ${V2.hairSoft}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontSize: 12.5, color: V2.mute }}>League type</span>
              <div role="radiogroup" aria-label="League type" style={{ display: 'flex', gap: 6 }}>
                {LEAGUE_TYPE_OPTIONS.map((o) => {
                  const on = (fields.leagueType || 'redraft') === o.value
                  return (
                    <button
                      key={o.value} type="button" role="radio" aria-checked={on}
                      onClick={() => onFieldChange('leagueType', o.value)}
                      style={{
                        fontSize: 12, fontWeight: 700, borderRadius: 7, padding: '4px 10px', cursor: 'pointer',
                        border: `1px solid ${on ? V2.blue : V2.hairSoft}`, background: on ? V2.blue : 'white', color: on ? 'white' : V2.navy,
                      }}
                    >
                      {o.label}
                    </button>
                  )
                })}
              </div>
            </div>
            {LEAGUE_TYPE_HINT[fields.leagueType] && (
              <div style={{ fontSize: 11.5, color: V2.mute, marginTop: 6, lineHeight: 1.5 }}>{LEAGUE_TYPE_HINT[fields.leagueType]}</div>
            )}
          </div>
          <div style={{ padding: '10px 0', borderBottom: `1px solid ${V2.hairSoft}` }}>
            <label htmlFor="rookies-only-input" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, cursor: 'pointer' }}>
              <span style={{ fontSize: 12.5, color: V2.mute }}>Rookie-only draft</span>
              <input
                id="rookies-only-input" data-testid="rookies-only-input" type="checkbox"
                checked={!!fields.rookiesOnly}
                onChange={(e) => onFieldChange('rookiesOnly', e.target.checked)}
                style={{ width: 20, height: 20, accentColor: V2.blue, cursor: 'pointer' }}
              />
            </label>
            <div style={{ fontSize: 11.5, color: V2.mute, marginTop: 6, lineHeight: 1.5 }}>
              Only this year's drafted rookies can be picked (common in dynasty leagues). JAI will build strategies from the rookie class only.
            </div>
          </div>
          <div style={{ padding: '10px 0', borderBottom: `1px solid ${V2.hairSoft}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <label htmlFor="draft-date-input" style={{ fontSize: 12.5, color: V2.mute }}>Draft date and time (optional)</label>
              <input
                id="draft-date-input" data-testid="draft-date-input" type="datetime-local"
                value={isoToLocalInput(fields.draftDate)}
                onChange={(e) => onFieldChange('draftDate', localInputToIso(e.target.value))}
                style={{
                  fontSize: 12.5, fontWeight: 700, color: V2.navy, border: `1px solid ${V2.hairSoft}`, borderRadius: 7,
                  padding: '4px 8px', fontFamily: JAI_FONTS.tight, outline: 'none', maxWidth: '100%',
                }}
              />
            </div>
            <div style={{ fontSize: 11.5, color: V2.mute, marginTop: 6, lineHeight: 1.5 }}>
              ESPN and Sleeper tell JAI when your draft is; Yahoo doesn't. If yours is blank, add it and JAI will count down to it and remind you before it starts.
            </div>
          </div>
          {blankFields(fields).length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: V2.error }}>
              JAI couldn't read {blankFields(fields).map((f) => FIELD_LABELS[f].toLowerCase()).join(' or ')} from your league. Enter {blankFields(fields).length > 1 ? 'them' : 'it'} above to continue.
            </div>
          )}
          {previewPicks.length > 0 && (
            <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {previewPicks.map((p) => (
                <span key={p.overallPick} style={{ fontSize: 11, fontWeight: 700, color: V2.mute, padding: '3px 8px 3px 0', fontFamily: JAI_FONTS.mono }}>
                  R{p.round} · {p.overallPick}
                </span>
              ))}
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12.5, color: V2.mute, marginBottom: 6 }}>League notes (optional)</div>
            <textarea
              value={fields.notes || ''}
              onChange={(e) => onFieldChange('notes', e.target.value.slice(0, NOTES_MAX_LENGTH))}
              placeholder="Anything unusual about your league's rules — keeper picks, salary cap, third round reversal, custom roster slots..."
              rows={3}
              maxLength={NOTES_MAX_LENGTH}
              style={{
                width: '100%', resize: 'vertical', boxSizing: 'border-box',
                border: `1px solid ${V2.hairSoft}`, borderRadius: 8, padding: '8px 10px',
                fontSize: 12.5, fontFamily: JAI_FONTS.tight, outline: 'none', color: V2.navy,
              }}
            />
            <div style={{ fontSize: 11, color: V2.faint, marginTop: 4, textAlign: 'right' }}>
              {(fields.notes || '').length}/{NOTES_MAX_LENGTH}
            </div>
            {draftTypeSuggestion && draftTypeSuggestion !== fields.draftType && (
              <div style={{
                marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                borderLeft: '3px solid #9A6A00', borderRadius: 0, padding: '2px 0 2px 10px',
              }}>
                <span style={{ fontSize: 12, color: V2.navy }}>
                  Your notes mention {DRAFT_TYPE_DISPLAY[draftTypeSuggestion] || draftTypeSuggestion} — update Draft Type to match?
                </span>
                <button
                  onClick={applyDraftTypeSuggestion}
                  style={{ background: V2.blue, color: 'white', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
                >
                  Update
                </button>
              </div>
            )}
          </div>
        </div>
      </MascotDialog>
    )
  }

  if (step === 'locking') {
    return (
      <MascotDialog
        fullPage state={locking ? 'thinking' : 'happy'} size={280}
        headline={locking ? 'Locking in your strategy' : 'STRATEGY LOCKED'}
        actions={locking ? [] : [
          { label: 'Go to Live Draft', primary: true, onClick: () => navigate(`/league/${id}/draft/live`) },
          { label: 'View your strategy', onClick: () => { setCtx((p) => ({ ...p, state: 'B' })) } },
        ]}
      />
    )
  }

  if (step === 'detail') {
    if (detailLoading || !detail) {
      return pageShell(
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <JAIMascot size={56} state="thinking" animate />
          </div>
        </div>
      )
    }
    return pageShell(
      <StrategyDetailView
        league={league} strategy={detail} mode="candidate"
        totalRounds={ctx?.context?.totalRounds}
        onConfirm={handleLock} confirming={locking}
        onBack={() => setStep('list')}
      />
    )
  }

  // step === 'list'
  return pageShell(
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', marginBottom: 26 }}>
        <JAIMascot size={64} state={generating || customLoading ? 'thinking' : 'focused'} animate />
        <h1 style={{ fontSize: 24, fontWeight: 800, color: V2.navy, margin: 0 }}>Implement Your Strategy</h1>
        {ctx?.context?.leagueType && ctx.context.leagueType !== 'redraft' && (
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: V2.blue, padding: 0 }}>
            {ctx.context.leagueType} league
          </span>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', marginTop: -10, marginBottom: 22 }}>
        <button
          type="button" data-testid="pick-trades-open"
          onClick={() => setShowPickTrades(true)}
          style={{
            minHeight: 52, padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700, fontFamily: JAI_FONTS.tight,
            border: `1px solid ${V2.hairSoft}`, background: 'white', color: V2.navy, cursor: 'pointer',
          }}
        >
          Pick trades
        </button>
        <button
          type="button" data-testid="edit-league-details"
          onClick={() => startEditingDetails()}
          disabled={generating || customLoading}
          style={{
            minHeight: 52, padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700, fontFamily: JAI_FONTS.tight,
            border: `1px solid ${V2.hairSoft}`, background: 'white', color: V2.navy,
            cursor: generating || customLoading ? 'default' : 'pointer', opacity: generating || customLoading ? 0.5 : 1,
          }}
        >
          Edit league details
        </button>
      </div>

      <div style={{ display: 'flex', gap: 28 }}>
        <div style={{ width: 100, flexShrink: 0, position: 'sticky', top: 20, alignSelf: 'flex-start' }}>
          {(previewPicks || []).map((p) => (
            <div key={p.overallPick} style={{ fontSize: 11, fontWeight: 700, color: V2.mute, fontFamily: JAI_FONTS.mono, marginBottom: 6 }}>
              R{p.round} · {p.overallPick}
            </div>
          ))}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <V2SectionHead>JAI's Recommendations</V2SectionHead>
          {strategiesOfflineInfo && <OfflineBanner cachedAt={strategiesOfflineInfo.cachedAt} />}

          {strategiesFailed ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <JAIMascot size={56} state="idle" animate={false} />
              </div>
              <div style={{ fontSize: 12.5, color: V2.mute, marginTop: 10, marginBottom: 14 }}>
                JAI didn't finish building your strategies.
              </div>
              <button
                onClick={retryStrategies}
                style={{ background: V2.blue, color: 'white', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              {strategies.length > 0 && (

            <div style={{ borderTop: `1px solid ${V2.hairSoft}` }}>
              {strategies.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => openStrategy(s.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 16, textAlign: 'left',
                    padding: '16px 6px', borderBottom: `1px solid ${V2.hairSoft}`, background: 'none', border: 'none',
                    borderBottomStyle: 'solid', cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 20, fontWeight: 800, color: V2.faint, fontFamily: JAI_FONTS.mono, width: 32, flexShrink: 0 }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: V2.navy, marginBottom: 2 }}>{s.name}</div>
                    <div style={{ fontSize: 12.5, color: V2.mute, marginBottom: 6 }}>{s.summary}</div>
                    <div style={{ display: 'flex', gap: 14, fontSize: 11.5, fontWeight: 600, color: V2.mute }}>
                      <span>{s.playerType}</span>
                      <span style={{ color: pctTone(s.riskPercent, { invert: true }), fontWeight: 700 }}>{s.riskPercent}% risk</span>
                      <span style={{ color: pctTone(s.successProbability), fontWeight: 700 }}>{s.successProbability}% available</span>
                    </div>
                  </div>
                  <span style={{ color: V2.faint }}>&rarr;</span>
                </button>
              ))}
            </div>
              )}
              {generating && (
                <JaiWorking
                  compact={strategies.length > 0}
                  headline={strategies.length > 0 ? 'JAI is still building…' : 'JAI is building your strategies'}
                  done={buildProgress?.done ?? strategies.length}
                  total={buildProgress?.total ?? strategyCount ?? 0}
                  sub={strategies.length > 0
                    ? 'Open one while the rest finish — each strategy appears the moment it is ready.'
                    : 'Each strategy appears here the moment it is ready. This can take a minute or two.'}
                />
              )}
            </>
          )}

          {!generating && !strategiesFailed && strategyCount != null && strategies.length >= strategyCount && strategyCount < 5 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
              <JAIMascot size={40} state="idle" animate={false} />
              <div style={{ fontSize: 12.5, color: V2.mute }}>
                There {5 - strategyCount === 1 ? 'is one more angle' : `are ${5 - strategyCount} more angles`} on this draft I could show you.
                {/* Native: a plain fact, no CTA and no domain (Guideline
                    3.1.1) — same pattern as the custom-turns notice just
                    below. Previously this sentence just trailed off on
                    native with nothing after it, reading as a broken
                    thought rather than a stated limit. */}
                {IS_NATIVE_APP
                  ? " That's as many as your current plan includes."
                  : <>{' '}<a href="/pricing" style={{ color: V2.blue, fontWeight: 700 }}>Upgrade</a></>}
              </div>
            </div>
          )}

          {!generating && !strategiesFailed && (
            <div style={{ marginTop: 26 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && submitCustomPrompt()}
                  disabled={customLoading || (customTurns.limit != null && customTurns.used >= customTurns.limit)}
                  placeholder="Or tell JAI the kind of strategy you want"
                  style={{ flex: 1, minWidth: 0, border: `1px solid ${V2.hairSoft}`, borderRadius: 10, padding: '10px 14px', fontSize: 13, fontFamily: JAI_FONTS.tight, outline: 'none' }}
                />
                <button
                  onClick={submitCustomPrompt}
                  disabled={customLoading || !customPrompt.trim() || (customTurns.limit != null && customTurns.used >= customTurns.limit)}
                  style={{ flexShrink: 0, background: V2.blue, color: 'white', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                >
                  {customLoading ? 'Thinking…' : 'Send'}
                </button>
              </div>
              {customLoading && (
                <JaiWorking compact headline="JAI is shaping your custom strategy" sub="It will appear in the list above when it is ready." />
              )}
              {customTurns.limit != null && customTurns.used >= customTurns.limit && (
                <div style={{ fontSize: 12, color: V2.mute, marginTop: 8 }}>
                  {IS_NATIVE_APP
                    ? "You've used all the refinements on your plan for this draft."
                    : <>Upgrade to keep refining this. <a href="/pricing" style={{ color: V2.blue, fontWeight: 700 }}>Upgrade</a></>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
